import Layout from 'components/layout';

import { BounceDetection, i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { Heading } from 'lucid-components-react';
import React from 'react';

import FooterAbTest from 'components/ab-tests/footer-swap/footer-swap';
import NavbarAbTest from 'components/ab-tests/nav-swap/nav-swap';
import BackNForth from 'components/back-n-forth/back-n-forth';
import ChartHeader from 'components/chart-header/chart-header';
import EmailOnly, {
  EmailOnlyLayouts,
  EmailOnlyThemes
} from 'components/email-only/email-only';
import EnterpriseVideo from 'components/enterprise-video/enterprise-video';
import FeatureGrid from 'components/feature-grid/feature-grid';
import PreFooterCta from 'components/pre-footer-cta/pre-footer-cta';
import LayoutContext from 'contexts/layoutContext';
import {
  Drupal_FieldNodePartnersFieldBackNForthSection,
  Drupal_NodeEnterprise,
  Drupal_ParagraphChartHeaderSection,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphEnterpriseVideo,
  Drupal_ParagraphFeatureGridSection,
  Drupal_ParagraphOrCallCta,
  Drupal_ParagraphPreFooterSection
} from 'generated/graphql';
import {
  useMenuEnterpriseFooter,
  useMenuEnterpriseNavbar
} from 'hooks/use-menu';
import { TemplateProps, isDefined } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';

const Enterprise = ({ data, pageContext, location }: TemplateProps) => {
  const { langPrefix, pageType, nid, i18nKey, hideSsr } = pageContext;

  const { drupal, allEnterpriseHeaderLogosJson } = data;
  const node = drupal.nodeById as Drupal_NodeEnterprise;
  const { entityMetatags, entityTranslations } = getEntityFields(node);
  const { i18nData } = data;

  const logoSets = allEnterpriseHeaderLogosJson!.edges;

  const langLogoSet = logoSets.find(logoSet => {
    return logoSet.node.lang === langPrefix;
  }) || logoSets[0];

  const logos = langLogoSet.node.logos || [];

  const chartHeader = node.fieldChartHeaderSection
    ? (node.fieldChartHeaderSection
      .entity as Drupal_ParagraphChartHeaderSection)
    : null;
  const videoSection = node.fieldVideoSection
    ? (node.fieldVideoSection.entity as Drupal_ParagraphEnterpriseVideo)
    : null;
  const backNForthSections =
    node.fieldBackNForthSection!.length > 0
      ? (node.fieldBackNForthSection as Drupal_FieldNodePartnersFieldBackNForthSection[])
      : null;
  const featureGrid = node.fieldFeatureGridSection
    ? (node.fieldFeatureGridSection
      .entity as Drupal_ParagraphFeatureGridSection)
    : null;
  const preFooter = node.fieldPreFooterSection
    ? (node.fieldPreFooterSection.entity as Drupal_ParagraphPreFooterSection)
    : null;

  const preFooterCtas = preFooter
    ? preFooter
      .fieldCtaButton!.filter(isDefined)
      .map(button => button.entity as Drupal_ParagraphCtaButton)
    : null;

  const preFooterOrCall =
    preFooter && preFooter.fieldOrCallCta
      ? (preFooter.fieldOrCallCta.entity as Drupal_ParagraphOrCallCta)
      : null;

  const hasBounceDetection = node.fieldHasBounceDetection;
  const i18n = i18nFunct(i18nData, i18nKey);

  const navbarMenu = useMenuEnterpriseNavbar(langPrefix);
  const footerMenu = useMenuEnterpriseFooter(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <NavbarAbTest overwriteMenu={navbarMenu} />
      <LayoutContext.Consumer>
        {({ language }) => (
          <main className={`enterprise nid-${pageContext.nid}`}>
            {chartHeader && <ChartHeader data={chartHeader} logos={logos} />}

            {videoSection && videoSection.fieldTopOfPage && (
              <EnterpriseVideo fields={videoSection} />
            )}

            {backNForthSections && (
              <BackNForth
                sections={backNForthSections}
                useJumplinks={node.fieldUseJumplinks}
              />
            )}

            {featureGrid && <FeatureGrid data={featureGrid} />}

            {videoSection && !videoSection.fieldTopOfPage && (
              <EnterpriseVideo fields={videoSection} />
            )}

            {preFooter && (
              <PreFooterCta
                ctas={preFooterCtas}
                header={preFooter.fieldHeading}
                orCallCta={preFooterOrCall}
                pitch={preFooter.fieldPitch}
                subheader={preFooter.fieldSubheader}
                variant={preFooter.fieldIsWhiteTheme ? 'light' : 'dark'}
              />
            )}
            {hasBounceDetection && (
              <BounceDetection i18nKey={language.i18nKey}>
                <Heading
                  as="h2"
                  className="bounce-detection__header"
                  mt={3}
                  variant="subheadS"
                >
                  {i18n('mktg-bounce-detection-header')}
                </Heading>
                <p
                  className="bounce-detection__body"
                  dangerouslySetInnerHTML={{
                    __html: i18n('mktg-bounce-detection-p1', {}, [
                      '<em>{}</em>'
                    ])
                  }}
                />
                <EmailOnly
                  bounceDetection
                  hideSSO
                  buttonText={i18n('mktg-subfooter-cta-secondary')}
                  layout={EmailOnlyLayouts.Stacked}
                  pageType={pageType}
                  theme={EmailOnlyThemes.Retro}
                />
              </BounceDetection>
            )}
          </main>
        )}
      </LayoutContext.Consumer>
      <FooterAbTest entityTranslations={entityTranslations} menu={footerMenu} />
    </Layout>
  );
};

export default Enterprise;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeEnterprise {
          ...entityFields
          fieldChartHeaderSection {
            entity {
              ... on Drupal_ParagraphChartHeaderSection {
                fieldTitle
                fieldTitlePiece
                fieldSubtitle
                fieldDescription
                fieldShowLogos
                fieldCtaButtons {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonText
                      fieldButtonLink
                      fieldIsSecondary
                      fieldMarketo {
                        entity {
                          ...marketo
                        }
                      }
                    }
                  }
                }
                fieldPhoneCta {
                  entity {
                    ... on Drupal_ParagraphOrCallCta {
                      fieldCallCtaPhoneNumber
                      fieldCallCtaText
                    }
                  }
                }
              }
            }
          }
          fieldUseJumplinks
          fieldBackNForthSection {
            entity {
              ... on Drupal_ParagraphBackNForthSection {
                fieldTitle
                fieldSectionDescription {
                  processed
                }
                fieldSectionImage {
                  entity {
                    ... on Drupal_ParagraphSeoImage {
                      fieldImageLink
                      fieldAltText
                    }
                  }
                }
              }
            }
          }
          fieldHasBounceDetection
          fieldFeatureGridSection {
            entity {
              ... on Drupal_ParagraphFeatureGridSection {
                fieldSectionTitle
                fieldItemsPerRow
                fieldFeatureGridItem {
                  entity {
                    ... on Drupal_ParagraphFeatureGridItem {
                      fieldFeatureTitle
                      fieldFeatureDescription
                      fieldFeatureLink
                      fieldFeatureImage {
                        entity {
                          ... on Drupal_ParagraphSeoImage {
                            fieldImageLink
                            fieldAltText
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          fieldVideoSection {
            entity {
              ... on Drupal_ParagraphEnterpriseVideo {
                fieldUseDefaults
                fieldVideoBackgroundImage
                fieldVideoLink
                fieldTitle
                fieldQuote
                fieldText
                fieldTopOfPage
              }
            }
          }
          fieldPreFooterSection {
            entity {
              ... on Drupal_ParagraphPreFooterSection {
                fieldIsWhiteTheme
                fieldHeading
                fieldSubheader
                fieldPitch
                fieldCtaButton {
                  entity {
                    ... on Drupal_ParagraphCtaButton {
                      fieldButtonText
                      fieldButtonLink
                      fieldIsSecondary
                      fieldMarketo {
                        entity {
                          ...marketo
                        }
                      }
                    }
                  }
                }
                fieldOrCallCta {
                  entity {
                    ... on Drupal_ParagraphOrCallCta {
                      fieldCallCtaPhoneNumber
                      fieldCallCtaText
                    }
                  }
                }
              }
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
        }
      }

      footerMenu: menuLinkContentQuery(
        limit: 10000
        sort: [
          { field: "parent", direction: ASC }
          { field: "weight", direction: ASC }
        ]
        filter: {
          conditions: [
            {
              field: "bundle"
              value: "enterprise-footer"
              language: $LanguageId
              operator: EQUAL
            }
          ]
        }
      ) {
        entities {
          entityTranslation(language: $LanguageId) {
            entityUuid
            ... on Drupal_MenuLinkContent {
              id
              title
              parent
              link {
                url {
                  translate(language: $LanguageId) {
                    path
                  }
                }
              }
            }
            ... on Drupal_MenuLinkContentEnterpriseFooter {
              fieldMarketo {
                entity {
                  ...marketo
                }
              }
            }
          }
        }
      }
    }
    allEnterpriseHeaderLogosJson {
      edges {
        node {
          lang
          logos {
            url
            alt
          }
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_bounce_detection_header
            mktg_bounce_detection_p1
            mktg_subfooter_cta_secondary
          }
        }
      }
    }
  }
`;
